/**
 * Build a summary tree from spans, preserving parent/child relationships.
 * Only returns supported node types for the session viewer tree.
 */
export const summarizeSpanKind = (spanKind) => {
  const kind = (spanKind || 'unspecified').toLowerCase();
  if (kind === 'chain') return 'chain';
  if (kind === 'agent') return 'agent';
  if (kind === 'llm') return 'llm';
  if (kind === 'retriever') return 'retriever';
  if (kind === 'reranker') return 'retriever';
  if (kind === 'tool') return 'tool';
  return 'unspecified';
};

const ALLOWED_KINDS = new Set(['agent', 'llm', 'retriever', 'tool']);
const IGNORED_AGENT_NAMES = new Set([
  'agent',
  'autogen',
  'groupchatmanager',
  'conversableagent',
  'assistantagent',
  'userproxyagent'
]);

export const buildSummaryTree = (spans = [], { includeAllNodes = false } = {}) => {
  if (!Array.isArray(spans) || spans.length === 0) return [];

  const nodeMap = new Map();
  spans.forEach((span) => {
    nodeMap.set(span.span_id, {
      span,
      kind: summarizeSpanKind(span.span_kind),
      children: [],
    });
  });

  const roots = [];
  spans.forEach((span) => {
    const node = nodeMap.get(span.span_id);
    const parentId = span.parent_span_id;
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const filterTree = (nodes) => {
    const result = [];
    nodes.forEach((node) => {
      const filteredChildren = filterTree(node.children || []);
      const spanName = (node.span?.span_name || node.span?.name || '').trim().toLowerCase();
      const hasDetections = Array.isArray(node.span?.detections) && node.span.detections.length > 0;
      const shouldIgnoreAgent = node.kind === 'agent' && IGNORED_AGENT_NAMES.has(spanName) && !hasDetections;

      if (ALLOWED_KINDS.has(node.kind) && !shouldIgnoreAgent) {
        result.push({ ...node, children: filteredChildren });
      } else {
        result.push(...filteredChildren);
      }
    });
    return result;
  };

  if (includeAllNodes) {
    return roots;
  }

  return filterTree(roots);
};

/**
 * Returns ids (Set) and parentMap (Map<span_id, parent_span_id|null>) reflecting the
 * summary tree's promoted hierarchy — not the raw parent_span_id fields. Use this
 * so Timeline and Graph use the same parent-child relationships as the Outline view.
 */
export const buildSummaryData = (spans) => {
  const tree = buildSummaryTree(spans, { includeAllNodes: false });
  const ids = new Set();
  const parentMap = new Map();
  const walk = (nodes, parentId = null) => {
    nodes.forEach(n => {
      ids.add(n.span.span_id);
      parentMap.set(n.span.span_id, parentId);
      if (n.children?.length) walk(n.children, n.span.span_id);
    });
  };
  walk(tree);
  return { ids, parentMap };
};
